import { Component, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CartService, CartItem, CartItemCustomization } from '../../services/cart.service';

@Component({
  selector: 'app-cart-display',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './cart-display.component.html',
  styleUrl: './cart-display.component.scss'
})
export class CartDisplayComponent {
  private cartService = inject(CartService);
  private router = inject(Router);

  // Computed values from cart service
  cart = computed(() => this.cartService.cart());
  cartItems = computed(() => this.cart()?.items || []);
  totalItems = computed(() => this.cartService.totalItems());
  totalPrice = computed(() => this.cartService.totalPrice());
  isEmpty = computed(() => this.cartService.isEmpty());

  // Local signals
  showCart = signal(false);
  editingItem = signal<CartItem | null>(null);
  tempQuantity = signal(1);

  // Toggle cart visibility
  toggleCart(): void {
    this.showCart.set(!this.showCart());
  }

  closeCart(): void {
    this.showCart.set(false);
  }

  // Quantity management
  increaseQuantity(itemId: string): void {
    const item = this.cartItems().find(i => i.id === itemId);
    if (item) {
      this.cartService.updateQuantity(itemId, item.quantity + 1);
    }
  }

  decreaseQuantity(itemId: string): void {
    const item = this.cartItems().find(i => i.id === itemId);
    if (item && item.quantity > 1) {
      this.cartService.updateQuantity(itemId, item.quantity - 1);
    }
  }

  updateQuantity(itemId: string, quantity: number): void {
    if (quantity >= 1) {
      this.cartService.updateQuantity(itemId, quantity);
    } else {
      // If quantity becomes 0, remove item and potentially close cart
      this.removeItem(itemId);
    }
  }

  // Item management
  removeItem(itemId: string): void {
    this.cartService.removeItem(itemId);
    // Auto-close cart if becomes empty
    if (this.isEmpty()) {
      this.showCart.set(false);
    }
  }

  clearCart(): void {
    if (confirm('¿Estás seguro de que quieres vaciar el carrito?')) {
      this.cartService.clearCart();
      // Auto-close cart when cleared
      this.showCart.set(false);
    }
  }

  // Edit item (for future expansion)
  startEditingItem(item: CartItem): void {
    this.editingItem.set(item);
    this.tempQuantity.set(item.quantity);
  }

  cancelEditingItem(): void {
    this.editingItem.set(null);
    this.tempQuantity.set(1);
  }

  saveEditedItem(): void {
    const item = this.editingItem();
    if (item) {
      this.cartService.updateQuantity(item.id, this.tempQuantity());
      this.cancelEditingItem();
    }
  }

  // Handle input event for quantity
  onQuantityInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = parseInt(input.value, 10);
    if (!isNaN(value) && value >= 1) {
      this.tempQuantity.set(value);
    }
  }

  // Utility methods
  formatPrice(price: number): string {
    return this.cartService.formatPrice(price);
  }

  getCustomizationsText(customizations: CartItemCustomization[]): string {
    return this.cartService.getCustomizationsText(customizations);
  }

  getItemTotal(item: CartItem): number {
    return item.price * item.quantity;
  }

  // Checkout - navigate to order confirmation
  proceedToCheckout(): void {
    const cart = this.cartService.cart();
    if (!cart || cart.items.length === 0) return;

    this.closeCart();
    this.router.navigate(['/order-confirmation'], {
      state: { fromCart: true }
    });
  }

  // Pack helpers
  isPackItem(item: CartItem): boolean {
    return item.product_id.startsWith('pack-');
  }

  /** Extract pack group key from product_id like "pack-5-608" → "pack-5" */
  getPackKey(item: CartItem): string {
    const parts = item.product_id.split('-');
    return parts.length >= 2 ? `${parts[0]}-${parts[1]}` : item.product_id;
  }

  /** Remove all cart items belonging to the same pack instance */
  removePackGroup(item: CartItem): void {
    this.cartService.removePackGroup(this.getPackKey(item));
    if (this.isEmpty()) {
      this.showCart.set(false);
    }
  }

  // Quick actions
  duplicateItem(item: CartItem): void {
    this.cartService.addItem(
      item.product_id,
      item.name,
      item.price,
      item.category,
      item.customizations,
      item.notes,
      item.image
    );
  }
}